
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import Autoplay from "embla-carousel-autoplay";
import type { AutoplayType } from "embla-carousel-autoplay";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const fetchCoaches = async () => {
  const { data, error } = await supabase
    .from('coaches')
    .select('id, name_surname, university, photo_url, placement_types, placement_ranks')
    .eq('active', true);

  if (error) {
    console.error('Error fetching coaches:', error);
    throw error;
  }
  return data;
};

export const CoachCarousel = () => {
  const navigate = useNavigate();
  const { data: coaches = [], isLoading, error } = useQuery({
    queryKey: ['coaches'],
    queryFn: fetchCoaches,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
  });

  const plugin = Autoplay({ delay: 2000, stopOnInteraction: false }) as AutoplayType;

  const handleCoachClick = (coach: any) => {
    if (!coach?.id) return; // Prevent navigation if coach data is invalid
    navigate(`/coaches/`);
  };

  if (isLoading) {
    return (
      <section className="py-10 md:py-20 bg-gradient-to-r from-kampus-pink to-white">
        <div className="container mx-auto px-4 text-center">
          Loading coaches...
        </div>
      </section>
    );
  }

  if (error) {
    console.error('Error in CoachCarousel:', error);
    return (
      <section className="py-10 md:py-20 bg-gradient-to-r from-kampus-pink to-white">
        <div className="container mx-auto px-4 text-center text-red-600">
          Error loading coaches. Please try again later.
        </div>
      </section>
    );
  }

  return (
    <section className="py-10 md:py-20 bg-gradient-to-r from-kampus-pink to-white overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8 md:mb-16">
          <h2 className="text-2xl md:text-4xl font-bold mb-4">
            Türkiye'nin En İyi Üniversitelerinden Seçkin Mentörler
          </h2>
          <p className="text-base md:text-lg text-brand-primary/70 max-w-3xl mx-auto">
            Kampüs Mentörlük ekibi YKS'de kendini kanıtlayarak Türkiye'nin en prestijli üniversitelerine yerleşmiş seçkin mentörlerden oluşur.
          </p>
        </div>

        <Carousel
          opts={{
            align: "start",
            loop: true,
            dragFree: true,
            containScroll: "trimSnaps",
          }}
          plugins={[plugin]}
          className="w-full mb-8 md:mb-12"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {coaches.map((coach) => (
              <CarouselItem
                key={coach.id}
                className="pl-2 md:pl-4 basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4 cursor-pointer"
                onClick={() => handleCoachClick(coach)}
              >
                <div className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105">
                  <div className="relative aspect-[3/4]">
                    <img
                      src={getCoachPhotoUrl(coach.photo_url)}
                      alt={coach.name_surname}
                      className="w-full h-full object-cover object-center"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/coaches/missing.jpg';
                      }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 md:p-6">
                      <h3 className="text-xl md:text-2xl font-bold text-white mb-1">
                        {coach.name_surname || 'İsim Belirtilmemiş'}
                      </h3>
                      <p className="text-base font-semibold text-white/90 mb-1">
                        {coach.university || 'Üniversite Belirtilmemiş'}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {coach.placement_types && coach.placement_ranks && coach.placement_types.map((type: string, index: number) => (
                          <div key={`${type}-${index}`} className="flex items-center gap-1 text-white bg-black/40 rounded-full px-2 py-0.5 text-sm">
                            <span className="font-medium">{type}</span>
                            <span className="font-bold">{coach.placement_ranks[index]}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex" />
          <CarouselNext className="hidden md:flex" />
        </Carousel>

        <div className="text-center">
          <Button
            variant="default"
            size="lg"
            onClick={() => navigate('/coaches')}
            className="bg-brand-primary hover:bg-brand-primary/90 text-white"
          >
            Tüm Mentörleri Gör
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

const getCoachPhotoUrl = (photoName: string | null) => {
  if (!photoName) return '/coaches/missing.jpg';
  return `/coaches/${photoName}.jpg`;
};
